import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Form, Select, Tag } from 'tdesign-react';
import { connect } from 'react-redux';
import { useMemoizedFn } from 'ahooks';
import { useSearchParams } from 'react-router-dom';
import { find } from 'lodash';
import OperateTitle from '@/pages/Maintenance/Executive/components/OperateTitle';
import { clusterList, getEnvList } from '@/services/resourceService';
import { clusterTypeOptions } from '@/utils/constant';
import {
  fetchFlowAdvanced,
  fetchFlowGroups,
  fetchFlowSteps,
  setCurrentCluster,
  addToHostList,
  setCurrentEnv,
} from '@/slices/execFlowSlice';
import { isVoid, toTsOptions } from '@/utils/common';
import { getComponentHosts } from '@/services/execComponentService';

const { FormItem } = Form;

function SelectCluster({
  setCurrentCluster,
  fetchFlowSteps,
  fetchFlowGroups,
  fetchFlowAdvanced,
  setCurrentEnv,
  envId,
  addToHostList,
}) {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const [options, setOptions] = useState([]);
  const [clusterOptions, setClusterOptions] = useState([]);

  const flowType = Number(searchParams.get('flowType'));
  const flowId = Number(searchParams.get('flowId'));
  const paramsEnvId = searchParams.get('envId');
  const paramsClusterId = searchParams.get('clusterId');

  const loadClusterOptions = async (envId) => {
    const { data } = await clusterList({ envId, pageSize: 99999, startIndex: 1, flowType });
    const list = data.items.map((item) => ({
      ...item,
      value: item.id,
      label: item.name,
    }));
    return list;
  };

  // 加载必要数据，开始初始化页面展示
  const doInitPage = async (envId, clusterId) => {
    await fetchFlowGroups(flowId);

    form.setFieldsValue({
      envId,
      clusterId,
    });
    fetchFlowAdvanced({ flowId, envId, clusterId });
    const { payload } = await fetchFlowSteps({ flowId, envId, clusterId });
    // 无主机组的组件
    const singleNodeSteps = payload.filter((item) => isVoid(item.groupId) && item.choiceType === 2);
    Promise.all(
      singleNodeSteps.map(async (step) => {
        const { scriptId, stepId, id } = step;
        const { data } = await getComponentHosts({
          componentStencilId: scriptId,
          envId,
          clusterId,
        });

        addToHostList({ stepId, id, hosts: data });
      }),
    );
  };

  const doInit = useMemoizedFn(async () => {
    const { data } = await getEnvList();
    const list = data.envInfoList.map((item) => ({
      ...item,
      id: item.envId,
      value: item.envId,
      label: item.envName,
    }));
    setOptions(list);
    // url 传递envId 和 clusterId
    // 主要用于url跳转使用
    if (!isVoid(paramsEnvId) && !isVoid(paramsClusterId)) {
      const envId = Number(paramsEnvId);
      const clusterId = Number(paramsClusterId);
      // 初始化环境
      const env = find(list, { id: envId });
      setCurrentEnv(env);

      // 初始化集群
      const clusters = await loadClusterOptions(envId);
      setClusterOptions(clusters);
      setCurrentCluster(find(clusters, { id: clusterId }));

      doInitPage(envId, clusterId);
    }
  });

  useEffect(() => {
    doInit();
  }, [doInit]);

  const envChange = async (value, { selectedOptions }) => {
    setClusterOptions([]);
    setCurrentEnv(selectedOptions[0]);
    setCurrentCluster(null);
    form.reset({ fields: ['clusterId'] });
    const clusters = await loadClusterOptions(value);
    setClusterOptions(clusters);
  };

  // 类型字符串
  const clusterType = useMemo(() => {
    return find(clusterTypeOptions, { value: flowType })?.label;
  }, [flowType]);

  const clusterChange = async (value, { selectedOptions }) => {
    setCurrentCluster(selectedOptions[0]);
    doInitPage(envId, value);
  };

  return (
    <Root>
      <OperateTitle title='选择目标集群' operator={<Tag>类型：{clusterType}</Tag>} />
      <Form form={form} style={{ padding: '12px 0' }} layout='inline'>
        <FormItem name='envId' label='选择环境'>
          <Select
            options={toTsOptions(options)}
            popupProps={{ overlayInnerStyle: { width: 'auto' } }}
            onChange={envChange}
          />
        </FormItem>
        <FormItem name='clusterId' label='选择集群'>
          <Select
            options={toTsOptions(clusterOptions)}
            onChange={clusterChange}
            popupProps={{ overlayInnerStyle: { width: 'auto' } }}
          />
        </FormItem>
      </Form>
    </Root>
  );
}

export default connect(
  (state) => ({
    envId: state.execFlow.currentEnv?.id,
  }),
  {
    setCurrentCluster,
    fetchFlowSteps,
    setCurrentEnv,
    fetchFlowGroups,
    fetchFlowAdvanced,
    addToHostList,
  },
)(SelectCluster);

const Root = styled.div`
  border-radius: 8px;
  border: 1px solid #e1e1e1;
  margin-bottom: 16px;
`;
